import { useMemo, useState } from 'react';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { UserData } from '../../types';

interface SignupsChartProps {
  users: UserData[];
}

function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateLabel(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function SignupsChart({ users }: SignupsChartProps) {
  const [range, setRange] = useState<'30d' | 'all'>('all');

  const data = useMemo(() => {
    const countsByDay: Record<string, number> = {};
    let usersWithoutDate = 0;

    users.forEach(user => {
      if (user.createdAt) {
        const date = toLocalDateString(new Date(user.createdAt));
        countsByDay[date] = (countsByDay[date] || 0) + 1;
      } else {
        usersWithoutDate++;
      }
    });

    const sortedDates = Object.keys(countsByDay).sort();
    if (sortedDates.length === 0) return [];

    const allDates: string[] = [];
    const [sy, sm, sd] = sortedDates[0].split('-').map(Number);
    const start = new Date(sy, sm - 1, sd);
    const end = new Date();
    end.setHours(0, 0, 0, 0);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      allDates.push(toLocalDateString(d));
    }

    let cumulative = usersWithoutDate;
    const fullData = allDates.map(dateStr => {
      const dailySignups = countsByDay[dateStr] || 0;
      cumulative += dailySignups;
      return { date: formatDateLabel(dateStr), signups: dailySignups, total: cumulative };
    });

    if (range === '30d') return fullData.slice(-30);
    return fullData;
  }, [users, range]);

  return (
    <div className="chart-card">
      <div className="chart-header">
        <h3>Daily Signups</h3>
        <div className="chart-range-toggle">
          <button className={range === '30d' ? 'active' : ''} onClick={() => setRange('30d')}>30d</button>
          <button className={range === 'all' ? 'active' : ''} onClick={() => setRange('all')}>All</button>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" fontSize={11} tick={{ fill: '#6b7280' }} />
          <YAxis yAxisId="left" fontSize={11} tick={{ fill: '#6b7280' }} />
          <YAxis yAxisId="right" orientation="right" fontSize={11} tick={{ fill: '#6b7280' }} />
          <Tooltip />
          <Legend />
          <Bar yAxisId="right" dataKey="signups" fill="#93c5fd" name="Daily Signups" />
          <Line yAxisId="left" type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={2.5} dot={false} name="Total Users" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
